import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ApiError } from '@/shared/api';
import { toApiError, useApi } from '@/shared/api';
import type { PricingOwnershipField } from './DynamicPricingOwnershipSection';

interface ReleaseVariables {
  field: PricingOwnershipField;
  propertyId: string;
  date: string;
}

const targetKey = ({ propertyId, date }: Pick<ReleaseVariables, 'propertyId' | 'date'>) =>
  `${propertyId}:${date}`;

export function useDynamicPricingOwnershipRelease(propertyId?: string, date?: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [failures, setFailures] = useState<Record<string, ApiError>>({});
  const mutation = useMutation({
    mutationFn: ({ field, propertyId: targetPropertyId, date: targetDate }: ReleaseVariables) =>
      api.channex.releaseAriOwnership({
        propertyId: targetPropertyId,
        from: targetDate,
        to: targetDate,
        rate: field === 'rate',
        minimumStay: field === 'minimumStay',
      }),
    onMutate: (variables) =>
      setFailures((current) => {
        const key = targetKey(variables);
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      }),
    onSuccess: async () => {
      // Auch Verfügbarkeit & Vertrieb: dort zeigt die Zelle das Schloss „von Hand gesetzt“.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dynamic-pricing-calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['dynamic-pricing-calendar-detail'] }),
        queryClient.invalidateQueries({ queryKey: ['availability-distribution'] }),
      ]);
    },
    onError: async (error, variables) => {
      const apiError = await toApiError(error);
      setFailures((current) => ({ ...current, [targetKey(variables)]: apiError }));
    },
  });
  const currentTarget = propertyId && date ? targetKey({ propertyId, date }) : undefined;
  const activeVariables =
    mutation.isPending && mutation.variables && targetKey(mutation.variables) === currentTarget
      ? mutation.variables
      : undefined;
  return {
    release: (field: PricingOwnershipField) => {
      if (!mutation.isPending && propertyId && date) {
        mutation.mutate({ field, propertyId, date });
      }
    },
    releasingField: activeVariables?.field,
    releasePending: mutation.isPending,
    releaseError: currentTarget ? failures[currentTarget] : undefined,
  };
}
